import {
  Box,
  Chip,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ButtonComp from "../../../../components/Button";
import Input from "../../../../components/Input";
import theme from "../../../../theme";
import OtpPopup from "../../../../components/OtpPopup";
import VerifiedPopup from "../../../../components/VerifiedPopup";
import { useEffect, useState } from "react";
import check from "../../../../assets/images/check.svg";
import { useRelationOptions } from "../../../../hooks/useRelationOptions";
import { useCountryOptions } from "../../../../hooks/useCountryOptions";
import { useVerifiedStore } from "../../../../store/useVerifiedStore";
import { useOtpVerifiedStore } from "../../../../store/useOtpVerifiedStore";
import { usePolicyStore } from "../../../../store/usePolicyStore";
import {
  getCaseCreationPolicy,
  getCaseCreationPolicyBySearch,
  getPolicy,
} from "../../../../services/guest";
import { getOperationalLocations } from "../../../../services/dashboard";
import { useToast } from "../../../../hooks/useToast";
import { useOtp } from "../../../../hooks/useOtp";
import { verifyOtp } from "../../../../services/guest";
import { useCaseCreationStore } from "../../../../store/useCaseCreationStore";
import CircularProgress from "@mui/material/CircularProgress";
import {
  caseActionBarSx,
  caseButtonSx,
  caseFormCardSx,
  caseGridSx,
  caseLabelSx,
  caseNotificationStackSx,
  caseRadioGroupSx,
} from "./caseFormStyles";

const countryCodes = [
  { label: "South Africa (+27)", code: "+27" },
  { label: "India (+91)", code: "+91" },
  { label: "USA (+1)", code: "+1" },
  { label: "UK (+44)", code: "+44" },
];

type Props = {
  informantDetails: any;
  setInformantDetails: (data: any) => void;
  onNext: () => void;
  minimalCapture?: boolean;
  showBranchName?: boolean;
};

export default function InformantDetailsForm({
  informantDetails,
  setInformantDetails,
  onNext,
  minimalCapture = false,
  showBranchName = true,
}: Props) {
  const [countryCode, setCountryCode] = useState(
    informantDetails.countryCode || "+27",
  );
  const [otpValue, setOtpValue] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { relationOptions, loading } = useRelationOptions();
  const { countryOptions } = useCountryOptions();
  const showVerified = useVerifiedStore((state) => state.showVerified);
  const setShowVerified = useVerifiedStore((state) => state.setShowVerified);
  const { setIsOtpVerified, isOtpVerified } = useOtpVerifiedStore();
  const {
    packageSelection,
    setPackageSelection,
    policyNumber,
    setPolicyNumber,
    selectedLivesInsured,
    setSelectedLivesInsured,
    setDeceasedDetails,
  } =
    useCaseCreationStore();
  const policyData = usePolicyStore((state) => state.policyData);
  const setPolicyData = usePolicyStore((state) => state.setPolicyData);
  const { showInfoToast, showSuccessToast } = useToast();
  const { sendOtp } = useOtp();
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyError, setPolicyError] = useState("");
  const [policySearchType, setPolicySearchType] = useState<
    "POLICY_NUMBER" | "ID_NUMBER"
  >("POLICY_NUMBER");
  const [policyLookupValue, setPolicyLookupValue] = useState(policyNumber || "");
  const [memberIdSearchValue, setMemberIdSearchValue] = useState("");
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const cellPhoneNumber = `${countryCode}${informantDetails.contactNumber || ""}`;
  const sourceType = minimalCapture ? "MANUAL" : packageSelection.sourceType;
  const classificationSelected = Boolean(sourceType);

  useEffect(() => {
    let isMounted = true;

    const loadOperationalLocations = async () => {
      if (!showBranchName) return;
      setLocationsLoading(true);
      const response = await getOperationalLocations("BRANCH");
      if (isMounted) {
        const locations = response?.status === 200 && Array.isArray(response.data) ? response.data : [];
        setBranchOptions(locations);
        setLocationsLoading(false);
      }
    };

    void loadOperationalLocations();

    return () => {
      isMounted = false;
    };
  }, [showBranchName]);

  const extractSouthAfricanIdDetails = (id: string) => {
    if (!/^\d+$/.test(id) || id.length < 10) return null;

    const dob = id.substring(0, 6);
    const genderSequence = parseInt(id.substring(6, 10), 10);

    const year = parseInt(dob.substring(0, 2), 10);
    const month = parseInt(dob.substring(2, 4), 10);
    const day = parseInt(dob.substring(4, 6), 10);
    let fullYear = 2000 + year;
    if (new Date(fullYear, month - 1, day) > new Date()) {
      fullYear -= 100;
    }

    const date = new Date(fullYear, month - 1, day);
    if (
      date.getFullYear() !== fullYear ||
      date.getMonth() + 1 !== month ||
      date.getDate() !== day
    ) {
      return null;
    }

    return {
      dateOfBirth: `${fullYear}-${String(month).padStart(2, "0")}-${String(
        day,
      ).padStart(2, "0")}`,
      gender: genderSequence >= 5000 ? "Male" : "Female",
    };
  };

  const validateSouthAfricanID = (id: string): string | null => {
    // must be 13 digits
    if (!/^\d{13}$/.test(id)) return "ID number must be 13 digits";

    // Extract parts
    const dob = id.substring(0, 6);
    const ssss = parseInt(id.substring(6, 10), 10);
    const citizenship = parseInt(id.charAt(10), 10);

    // Validate DOB
    const year = parseInt(dob.substring(0, 2), 10);
    const month = parseInt(dob.substring(2, 4), 10);
    const day = parseInt(dob.substring(4, 6), 10);
    let fullYear = 2000 + year;
    if (new Date(fullYear, month - 1, day) > new Date()) {
      fullYear -= 100;
    }

    const date = new Date(fullYear, month - 1, day);
    if (
      date.getFullYear() !== fullYear ||
      date.getMonth() + 1 !== month ||
      date.getDate() !== day
    ) {
      return "Invalid date of birth in ID number";
    }

    // Validate gender sequence
    if (ssss < 0 || ssss > 9999) {
      return "Invalid gender sequence in ID number";
    }

    // Validate citizenship
    if (![0, 1, 2].includes(citizenship)) {
      return "Invalid citizenship digit in ID number";
    }

    // Luhn checksum
    const luhnCheck = (num: string) => {
      let sum = 0;
      let alt = false;
      for (let i = num.length - 1; i >= 0; i--) {
        let n = parseInt(num.charAt(i), 10);
        if (alt) {
          n *= 2;
          if (n > 9) n -= 9;
        }
        sum += n;
        alt = !alt;
      }
      return sum % 10 === 0;
    };

    if (!luhnCheck(id)) return "Invalid ID number checksum";

    return null;
  };

  const getPolicyMemberValue = (member: any, keys: string[]) => {
    for (const key of keys) {
      const value = member?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }
    return "";
  };

  const formatPolicyDate = (value: any) => {
    if (!value) return "";
    if (Array.isArray(value) && value.length >= 3) {
      const [year, month, day] = value;
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    if (typeof value === "object") {
      const year = value.year || value.Year;
      const month = value.monthValue || value.month || value.Month;
      const day = value.dayOfMonth || value.day || value.Day;
      if (year && month && day) {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      }
    }
    const stringValue = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(stringValue)) {
      return stringValue.slice(0, 10);
    }
    const parsed = new Date(stringValue);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
    return "";
  };

  const normalizeTitleValue = (value: any) => {
    const raw = String(value || "").trim();
    if (!raw) return "";

    const titleMap: Record<string, string> = {
      ADV: "Adv",
      DR: "Dr",
      MISS: "Miss",
      MR: "Mr",
      MRS: "Mrs",
      MS: "Ms",
      PROF: "Prof",
      REV: "Rev",
    };

    return titleMap[raw.toUpperCase()] || raw;
  };

  const getPolicyMainMember = () =>
    policyData?.mainMember ||
    policyData?.MainMember ||
    policyData?.main_member ||
    null;

  const normalizeIdentity = (value: any) =>
    String(value || "").replace(/\s+/g, "").toUpperCase();

  const getPolicyIdentitySearchNumber = () =>
    policyData?.casePortalIdentitySearchNumber ||
    policyData?.case_portal_identity_search_number ||
    "";

  const getPolicyNumberFromResponse = (policy: any) =>
    policy?.policy_number ||
    policy?.policyNumber ||
    policy?.mainMember?.policyNumber ||
    policy?.MainMember?.Policy_Number ||
    policy?.MainMember?.policyNumber ||
    "";

  const resolvePolicySearchType = (value: string) =>
    /^\d+$/.test(value.trim()) ? "ID_NUMBER" : "POLICY_NUMBER";

  const normalizeMemberCollection = (...values: any[]) => {
    for (const value of values) {
      if (Array.isArray(value)) {
        return value.filter(Boolean);
      }
      if (value && typeof value === "object") {
        const memberKeys = [
          "Member_ID",
          "memberId",
          "Name",
          "name",
          "First_Name",
          "firstName",
          "Surname",
          "surname",
          "ID_num",
          "ID_Number",
          "idNum",
          "idNumber",
        ];
        const looksLikeSingleMember = memberKeys.some((key) => key in value);
        if (looksLikeSingleMember) {
          return [value];
        }

        const objectValues = Object.values(value).filter(
          (item) => item && typeof item === "object",
        );
        if (objectValues.length > 0) {
          return objectValues as any[];
        }
      }
    }
    return [];
  };

  const getPolicyMemberName = (member: any) => {
    const title = getPolicyMemberValue(member, ["title", "Title"]);
    const firstName = getPolicyMemberValue(member, ["firstName", "name", "First_Name", "Name", "member_name"]);
    const lastName = getPolicyMemberValue(member, ["lastName", "surname", "Surname"]);
    return [title, firstName, lastName].filter(Boolean).join(" ").trim() || "Unnamed member";
  };

  const getPolicyMemberRelation = (member: any, fallback: string) => {
    const explicitRelation =
      getPolicyMemberValue(member, ["relationship", "relation", "BenRelation", "Relation"]) || "";
    if (explicitRelation) {
      return explicitRelation;
    }

    const memberRole = getPolicyMemberValue(member, [
      "casePortalMemberRole",
      "CasePortalMemberRole",
    ]);
    if (memberRole === "MAIN_MEMBER") return "MAIN MEMBER";
    if (memberRole === "SPOUSE") return "SPOUSE";
    if (memberRole === "CHILD") return "CHILD";
    if (memberRole === "EXTENDED_FAMILY") return "EXTENDED FAMILY";

    if (/^spouse/i.test(fallback || "")) return "SPOUSE";
    if (/^child/i.test(fallback || "")) return "CHILD";
    if (/^main member/i.test(fallback || "")) return "MAIN MEMBER";
    if (/^extended family/i.test(fallback || "")) return "EXTENDED FAMILY";
    return fallback;
  };

  const getPolicyMemberIdentifier = (member: any) =>
    getPolicyMemberValue(member, [
      "identificationNumber",
      "idNumber",
      "idNum",
      "ID_Number",
      "ID_num",
      "passportNumber",
      "passportNum",
      "PassportNumber",
      "Passport_num",
    ]);

  const getExistingInformantSnapshot = (member: any) =>
    member?.casePortalExistingInformant ||
    member?.CasePortalExistingInformant ||
    null;

  const getExistingDeceasedSnapshot = (member: any) =>
    member?.casePortalExistingDeceased ||
    member?.CasePortalExistingDeceased ||
    null;

  const buildOptionsWithCurrentValue = (options: string[], currentValue: any) => {
    const normalizedCurrentValue = String(currentValue || "").trim();
    const normalizedOptions = options.filter(Boolean);

    if (!normalizedCurrentValue) {
      return normalizedOptions;
    }

    return normalizedOptions.includes(normalizedCurrentValue)
      ? normalizedOptions
      : [normalizedCurrentValue, ...normalizedOptions];
  };

  const buildLivesInsuredOptions = () => {
    if (!policyData) return [];

    const options: Array<{ key: string; label: string; member: any }> = [];
    const pushMember = (member: any, relationFallback: string) => {
      if (!member) return;
      const identitySearchNumber = getPolicyIdentitySearchNumber();
      if (
        identitySearchNumber &&
        normalizeIdentity(getPolicyMemberIdentifier(member)) !==
          normalizeIdentity(identitySearchNumber)
      ) {
        return;
      }
      const key = `${relationFallback}-${options.length}`;
      const relation = getPolicyMemberRelation(member, relationFallback);
      const name = getPolicyMemberName(member);
      const identifier = getPolicyMemberIdentifier(member);

      options.push({
        key,
        label: [relation, name, identifier].filter(Boolean).join(" | "),
        member: {
          ...member,
          __caseLivesInsuredKey: key,
          __caseLivesInsuredRelation: relationFallback,
        },
      });
    };

    const mainMember = getPolicyMainMember();
    if (mainMember) {
      pushMember(mainMember, "Main Member");
    } else if (policyData.member_name || policyData.member_contact_number) {
      pushMember(
        {
          firstName: policyData.member_name,
          contactNumber: policyData.member_contact_number,
          title: policyData.member_title,
          initials: policyData.member_initials,
        },
        "Main Member",
      );
    }

    normalizeMemberCollection(
      policyData.spouse,
      policyData.Spouse,
      policyData.spouses,
      policyData.spouseDetails,
      policyData.spouse_details,
    ).forEach((member, index) => pushMember(member, `Spouse ${index + 1}`));

    normalizeMemberCollection(
      policyData.children,
      policyData.Children,
      policyData.child,
      policyData.childrens,
      policyData.childrenDetails,
      policyData.children_details,
    ).forEach((member, index) => pushMember(member, `Child ${index + 1}`));

    normalizeMemberCollection(
      policyData.extendedFamily,
      policyData.ExtendedFamily,
      policyData.ExtendedFamiy,
      policyData.extended_family,
      policyData.dependents,
      policyData.dependentDetails,
      policyData.dependent_details,
    ).forEach((member, index) =>
      pushMember(member, getPolicyMemberRelation(member, `Extended Family ${index + 1}`)),
    );

    return options;
  };

  const livesInsuredOptions = buildLivesInsuredOptions();
  const selectedLivesInsuredOption = selectedLivesInsured
    ? livesInsuredOptions.find(
      (option) =>
        option.key === selectedLivesInsured.__caseLivesInsuredKey ||
        getPolicyMemberIdentifier(option.member) === getPolicyMemberIdentifier(selectedLivesInsured),
    ) || null
    : null;

  const handleMemberIdSearch = (value: string) => {
    const normalizedValue = normalizeIdentity(value);
    setMemberIdSearchValue(value);
    if (!normalizedValue) {
      clearError("selectedLivesInsured");
      return;
    }

    const matchedOption = livesInsuredOptions.find(
      (option) =>
        normalizeIdentity(getPolicyMemberIdentifier(option.member)) === normalizedValue,
    );

    if (matchedOption) {
      handleLivesInsuredSelection(matchedOption.member);
      clearError("selectedLivesInsured");
      return;
    }

    setSelectedLivesInsured(null);
    setErrors((prev) => ({
      ...prev,
      selectedLivesInsured: "No insured member found with this ID number on the loaded policy",
    }));
  };

  const findMemberByIdentity = (policy: any, identityNumber: string) => {
    const normalizedIdentityNumber = normalizeIdentity(identityNumber);
    if (!normalizedIdentityNumber) return null;

    const withRelation = (member: any, relation: string) =>
      member
        ? {
            ...member,
            __caseLivesInsuredKey: `${relation}-0`,
            __caseLivesInsuredRelation: relation,
          }
        : null;

    const mainMember = policy?.mainMember || policy?.MainMember || policy?.main_member;
    if (
      mainMember &&
      normalizeIdentity(getPolicyMemberIdentifier(mainMember)) ===
        normalizedIdentityNumber
    ) {
      return withRelation(mainMember, "Main Member");
    }

    const spouse = normalizeMemberCollection(
      policy?.spouse,
      policy?.Spouse,
      policy?.spouses,
      policy?.spouseDetails,
      policy?.spouse_details,
    ).find(
      (member) =>
        normalizeIdentity(getPolicyMemberIdentifier(member)) ===
        normalizedIdentityNumber,
    );
    if (spouse) return withRelation(spouse, "Spouse 1");

    const child = normalizeMemberCollection(
      policy?.children,
      policy?.Children,
      policy?.child,
      policy?.childrens,
      policy?.childrenDetails,
      policy?.children_details,
    ).find(
      (member) =>
        normalizeIdentity(getPolicyMemberIdentifier(member)) ===
        normalizedIdentityNumber,
    );
    if (child) return withRelation(child, "Child 1");

    const extendedFamily = normalizeMemberCollection(
      policy?.extendedFamily,
      policy?.ExtendedFamily,
      policy?.ExtendedFamiy,
      policy?.extended_family,
      policy?.dependents,
      policy?.dependentDetails,
      policy?.dependent_details,
    ).find(
      (member) =>
        normalizeIdentity(getPolicyMemberIdentifier(member)) ===
        normalizedIdentityNumber,
    );
    if (extendedFamily) {
      return withRelation(
        extendedFamily,
        getPolicyMemberRelation(extendedFamily, "Extended Family 1"),
      );
    }

    return null;
  };
  const titleOptions = buildOptionsWithCurrentValue(
    ["Adv", "Dr", "Miss", "Mr", "Mrs", "Ms", "Prof", "Rev"],
    normalizeTitleValue(informantDetails.title),
  );
  const relationshipOptions = buildOptionsWithCurrentValue(
    relationOptions,
    informantDetails.relationship,
  );
  const nationalityOptions = buildOptionsWithCurrentValue(
    countryOptions,
    informantDetails.nationality,
  );
  const residenceOptions = buildOptionsWithCurrentValue(
    countryOptions,
    informantDetails.countryOfResidence,
  );

  const hydrateInformantFromSnapshot = (snapshot: any, overwrite = true) => {
    if (!snapshot) return;

    setInformantDetails({
      title: normalizeTitleValue(snapshot?.title) || (overwrite ? "" : informantDetails.title),
      initials: snapshot?.initials || (overwrite ? "" : informantDetails.initials),
      firstName: snapshot?.firstName || (overwrite ? "" : informantDetails.firstName),
      lastName: snapshot?.lastName || (overwrite ? "" : informantDetails.lastName),
      gender: snapshot?.gender || (overwrite ? "" : informantDetails.gender),
      dateOfBirth: formatPolicyDate(snapshot?.dateOfBirth) || (overwrite ? "" : informantDetails.dateOfBirth),
      identificationNumber: snapshot?.identificationNumber || (overwrite ? "" : informantDetails.identificationNumber),
      identificationType: snapshot?.identificationType || (overwrite ? "" : informantDetails.identificationType),
      contactNumber: snapshot?.contactNumber || (overwrite ? "" : informantDetails.contactNumber),
      email: snapshot?.email || (overwrite ? "" : informantDetails.email),
      branchName: showBranchName
        ? snapshot?.branchName || (overwrite ? "" : informantDetails.branchName)
        : "",
      relationship: snapshot?.relationship || (overwrite ? "" : informantDetails.relationship),
      nationality: snapshot?.nationality || (overwrite ? "" : informantDetails.nationality),
      countryOfResidence: snapshot?.countryOfResidence || (overwrite ? "" : informantDetails.countryOfResidence),
      address: snapshot?.address || (overwrite ? "" : informantDetails.address),
      postalCode: snapshot?.postalCode || informantDetails.postalCode,
      postalAddress: snapshot?.postalAddress || informantDetails.postalAddress,
    });
  };

  const hydrateDeceasedFromSnapshot = (snapshot: any) => {
    if (!snapshot) return;

    setDeceasedDetails({
      title: normalizeTitleValue(snapshot?.title),
      initials: snapshot?.initials || "",
      firstName: snapshot?.firstName || "",
      lastName: snapshot?.lastName || "",
      relationship: snapshot?.relationship || "",
      dateOfBirth: formatPolicyDate(snapshot?.dateOfBirth),
      dateOfDeath: formatPolicyDate(snapshot?.dateOfDeath),
      identificationNumber: snapshot?.identificationNumber || "",
      identificationType:
        snapshot?.identificationType ||
        (snapshot?.passportNumber && !snapshot?.identificationNumber ? "passport" : "id"),
      gender: snapshot?.gender || "",
      causeOfDeath: snapshot?.causeOfDeath || "",
    });
  };

  const clearPolicyDrivenDetails = () => {
    setInformantDetails({
      title: "",
      initials: "",
      firstName: "",
      lastName: "",
      gender: "",
      dateOfBirth: "",
      identificationNumber: "",
      branchName: "",
      relationship: "",
      nationality: "",
      countryOfResidence: "",
      email: "",
      address: "",
      contactNumber: "",
      identificationType: "id",
    });
    setDeceasedDetails({});
    setIsOtpVerified(false);
  };

  const clearInformantPolicyDrivenDetails = () => {
    setInformantDetails({
      title: "",
      initials: "",
      firstName: "",
      lastName: "",
      gender: "",
      dateOfBirth: "",
      identificationNumber: "",
      branchName: "",
      relationship: "",
      nationality: "",
      countryOfResidence: "",
      email: "",
      address: "",
      contactNumber: "",
      identificationType: "id",
    });
    setIsOtpVerified(false);
  };

  const hydrateDeceasedFromPolicyMember = (policyMember: any) => {
    if (!policyMember) return;

    const identifier = getPolicyMemberIdentifier(policyMember);
    const passportNumber = getPolicyMemberValue(policyMember, [
      "passportNumber",
      "passportNum",
      "PassportNumber",
      "Passport_num",
    ]);

    setDeceasedDetails({
      title: getPolicyMemberValue(policyMember, ["title", "Title"]),
      initials: getPolicyMemberValue(policyMember, ["initials", "Initials"]),
      firstName: getPolicyMemberValue(policyMember, ["firstName", "name", "First_Name", "Name", "member_name"]),
      lastName: getPolicyMemberValue(policyMember, ["lastName", "surname", "Surname"]),
      relationship:
        getPolicyMemberRelation(
          policyMember,
          policyMember.__caseLivesInsuredRelation || "",
        ) || "",
      dateOfBirth: formatPolicyDate(
        getPolicyMemberValue(policyMember, [
          "dateOfBirth",
          "Date_Of_Birth",
          "Date_of_Birth",
          "date_of_birth",
          "BenDOB",
        ]),
      ),
      dateOfDeath: formatPolicyDate(
        getPolicyMemberValue(policyMember, [
          "dateOfDeath",
          "Date_Of_Death",
          "Date_of_Death",
          "date_of_death",
        ]),
      ),
      identificationNumber: identifier || "",
      identificationType:
        getPolicyMemberValue(policyMember, ["identificationType"]) ||
        (identifier && !passportNumber ? "id" : passportNumber ? "passport" : "id"),
      gender: getPolicyMemberValue(policyMember, ["gender", "Gender"]) || "",
    });
  };

  const handleClientTypeChange = (nextSourceType: "MANUAL" | "POLICY") => {
    setPackageSelection({ sourceType: nextSourceType });
    setPolicyError("");
    setErrors({});
    clearPolicyDrivenDetails();
    setSelectedLivesInsured(null);
    setMemberIdSearchValue("");
    setPolicyData(null);
    if (nextSourceType === "MANUAL") {
      setPolicyLookupValue("");
      setPolicyNumber("");
      setDeceasedDetails({});
    }
  };

  const handleLivesInsuredSelection = (member: any) => {
    setSelectedLivesInsured(member);
    setPolicyError("");
    clearError("selectedLivesInsured");

    const selectedInformantSnapshot = getExistingInformantSnapshot(member);

    if (selectedInformantSnapshot) {
      hydrateInformantFromSnapshot(selectedInformantSnapshot);
    } else {
      clearInformantPolicyDrivenDetails();
    }

    const selectedDeceasedSnapshot = getExistingDeceasedSnapshot(member);
    if (selectedDeceasedSnapshot) {
      hydrateDeceasedFromSnapshot(selectedDeceasedSnapshot);
    } else {
      hydrateDeceasedFromPolicyMember(member);
    }
  };

  const handleLoadPolicy = async () => {
    const trimmedSearchValue = (policyLookupValue || "").trim();
    const activeSearchType = resolvePolicySearchType(trimmedSearchValue);
    setPolicySearchType(activeSearchType);
    if (!trimmedSearchValue) {
      setPolicyError("Please enter a policy number or SA ID number");
      return;
    }
    if (activeSearchType === "ID_NUMBER") {
      const idError = validateSouthAfricanID(trimmedSearchValue);
      if (idError) {
        setPolicyError(idError);
        return;
      }
    }

    setPolicyLoading(true);
    setPolicyError("");
    try {
      let response =
        activeSearchType === "ID_NUMBER"
          ? await getCaseCreationPolicyBySearch(
              "DEATH",
              "ID_NUMBER",
              trimmedSearchValue,
            )
          : await getCaseCreationPolicy("DEATH", trimmedSearchValue);
      if (response?.status !== 200 && activeSearchType === "POLICY_NUMBER") {
        response = await getPolicy("DEATH", trimmedSearchValue);
      }
      if (response?.status === 200) {
        const policy = response?.data?.data || response?.data;
        setPolicyData(policy);
        const resolvedPolicyNumber = getPolicyNumberFromResponse(policy);
        if (resolvedPolicyNumber) {
          setPolicyNumber(resolvedPolicyNumber);
          if (activeSearchType === "POLICY_NUMBER") {
            setPolicyLookupValue(resolvedPolicyNumber);
          }
        }
        setSelectedLivesInsured(null);
        setMemberIdSearchValue("");
        setPackageSelection({ sourceType: "POLICY" });
        clearPolicyDrivenDetails();
        if (activeSearchType === "ID_NUMBER") {
          const matchedMember = findMemberByIdentity(policy, trimmedSearchValue);
          if (matchedMember) {
            handleLivesInsuredSelection(matchedMember);
            setMemberIdSearchValue(trimmedSearchValue);
          }
        }
        showSuccessToast("Policy loaded successfully");
      } else {
        setPolicyData(null);
        setSelectedLivesInsured(null);
        setMemberIdSearchValue("");
        setPolicyError(
          activeSearchType === "ID_NUMBER"
            ? "No policy was found for this ID number. For dependents or extended family, load the policy by policy number and select the insured member from the policy list."
            : response?.data?.message || "Unable to load policy",
        );
      }
    } catch (error) {
      console.error("Error loading policy", error);
      setPolicyError(
        activeSearchType === "ID_NUMBER"
          ? "No policy was found for this ID number. For dependents or extended family, load the policy by policy number and select the insured member from the policy list."
          : "Unable to load policy",
      );
      setPolicyData(null);
    } finally {
      setPolicyLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!sourceType && !minimalCapture) {
      newErrors.sourceType = "Choose Cash Client or Policy Holder";
      setErrors(newErrors);
      return false;
    }

    if (!informantDetails.title) newErrors.title = "Title is required";

    if (!informantDetails.firstName)
      newErrors.firstName = "First name is required";

    if (!informantDetails.lastName)
      newErrors.lastName = "Last name is required";

    if (!minimalCapture && !informantDetails.gender) newErrors.gender = "Gender is required";

    if (!minimalCapture && !informantDetails.dateOfBirth)
      newErrors.dateOfBirth = "Date of birth is required";

    if (showBranchName && !informantDetails.branchName)
      newErrors.branchName = "Branch name is required";

    if (!informantDetails.relationship)
      newErrors.relationship = "Relationship is required";

    if (!minimalCapture && !informantDetails.nationality)
      newErrors.nationality = "Nationality is required";

    if (!minimalCapture && !informantDetails.countryOfResidence)
      newErrors.countryOfResidence = "Country of residence is required";

    if (informantDetails.email && !/^\S+@\S+\.\S+$/.test(informantDetails.email))
      newErrors.email = "Invalid email format";

    if (!minimalCapture && !informantDetails.address) newErrors.address = "Address is required";

    if (!informantDetails.contactNumber)
      newErrors.contactNumber = "Contact number is required";

    if (!minimalCapture && !isOtpVerified)
      newErrors.contactNumber = "Please verify your cellphone number";

    if (
      !minimalCapture && sourceType === "POLICY" &&
      !policyNumber.trim() &&
      !policyLookupValue.trim()
    ) {
      newErrors.policyNumber =
        policySearchType === "ID_NUMBER"
          ? "ID number is required"
          : "Policy number is required";
    }

    if (!minimalCapture && sourceType === "POLICY" && !policyData) {
      newErrors.policyNumber = "Load the policy before proceeding";
    }

    if (!minimalCapture && sourceType === "POLICY" && !selectedLivesInsured) {
      newErrors.selectedLivesInsured = "Select the insured member linked to this policy";
    }

    // South African ID validation
    if (
      informantDetails.identificationType === "id" &&
      informantDetails.identificationNumber
    ) {
      const idError = validateSouthAfricanID(
        informantDetails.identificationNumber,
      );
      if (idError) newErrors.identificationNumber = idError;
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleNextClick = () => {
    const isValid = validateForm();

    if (!isValid) return;

    if (!minimalCapture && !isOtpVerified) {
      showInfoToast("Please verify OTP before proceeding.");
      return;
    }
    onNext();
  };

  const clearError = (field: string) => {
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const handleOtpVerification = async () => {
    if (!informantDetails.contactNumber) {
      showInfoToast("Please enter your cellphone number before requesting OTP");
      return;
    }

    if (informantDetails.contactNumber.length < 8) {
      showInfoToast("Please enter a valid cellphone number");
      return;
    }
    setShowOtp(true);
    const cellPhoneNumber = `${countryCode}${informantDetails.contactNumber}`;
    await sendOtp(cellPhoneNumber);
  };

  const handleOtpVerify = async () => {
    const cellPhoneNumber = `${countryCode}${informantDetails.contactNumber}`;
    const res = await verifyOtp(cellPhoneNumber, otpValue);
    if (res.status === 200 && res.data.success === true) {
      setOtpValue("");
      setShowOtp(false);
      setIsOtpVerified(true);
      setShowVerified(true);
    } else if (res.data.success === false) {
      setOtpValue("");
      setShowOtp(false);
      showInfoToast(res.data.message);
    } else {
      showInfoToast(res.data.message);
    }
  };

  const handleVerifiedClose = () => {
    setShowVerified(false);
  };
  useEffect(() => {
    if (showVerified) {
      const timer = setTimeout(() => {
        setShowVerified(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showVerified, setShowVerified]);

  useEffect(() => {
    if (informantDetails.countryCode && informantDetails.countryCode !== countryCode) {
      setCountryCode(informantDetails.countryCode);
    }
  }, [informantDetails.countryCode, countryCode]);

  useEffect(() => {
    if (informantDetails.identificationType !== "id") return;

    const parsedDetails = extractSouthAfricanIdDetails(
      informantDetails.identificationNumber || "",
    );

    if (!parsedDetails) return;

    const updates: Record<string, string> = {};

    if (informantDetails.dateOfBirth !== parsedDetails.dateOfBirth) {
      updates.dateOfBirth = parsedDetails.dateOfBirth;
    }

    if (informantDetails.gender !== parsedDetails.gender) {
      updates.gender = parsedDetails.gender;
    }

    if (Object.keys(updates).length > 0) {
      setInformantDetails(updates);
    }
  }, [
    informantDetails.identificationNumber,
    informantDetails.identificationType,
    informantDetails.dateOfBirth,
    informantDetails.gender,
    setInformantDetails,
  ]);

  const canEditInformantForm =
    sourceType === "MANUAL" ||
    (sourceType === "POLICY" && Boolean(policyData) && Boolean(selectedLivesInsured));

  return (
    <Paper
      elevation={0}
      sx={caseFormCardSx}
    >
      {!minimalCapture && <Box
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: 2,
          p: 2,
          mb: 2.5,
          backgroundColor: "#FCFCFD",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
          <Box>
            <Typography fontSize={16} fontWeight={800} color="#111827" textAlign="left">
              Client Classification
            </Typography>
            <Typography fontSize={13} color="#6B7280" mt={0.5} textAlign="left">
              Choose the case source first. Policy Holder cases can load lives insured data from the policy record.
            </Typography>
            {errors.sourceType && (
              <Typography fontSize={12.5} color="error.main" mt={0.6} textAlign="left">
                {errors.sourceType}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1.25}>
            <Chip
              clickable
              label="Cash Client"
              onClick={() => handleClientTypeChange("MANUAL")}
              sx={{
                bgcolor: sourceType === "MANUAL" ? "#F97316" : "#FFFFFF",
                color: sourceType === "MANUAL" ? "#FFFFFF" : "#4B5563",
                fontWeight: 800,
                fontSize: 13.5,
                px: 1,
                height: 38,
                borderRadius: 999,
                border: sourceType === "MANUAL" ? "1px solid #F97316" : "1px solid #D1D5DB",
                "&:hover": {
                  bgcolor: sourceType === "MANUAL" ? "#EA6C0A" : "#F9FAFB",
                },
              }}
            />
            <Chip
              clickable
              label="Policy Holder"
              onClick={() => handleClientTypeChange("POLICY")}
              sx={{
                bgcolor: sourceType === "POLICY" ? "#F97316" : "#FFFFFF",
                color: sourceType === "POLICY" ? "#FFFFFF" : "#4B5563",
                fontWeight: 800,
                fontSize: 13.5,
                px: 1,
                height: 38,
                borderRadius: 999,
                border: sourceType === "POLICY" ? "1px solid #F97316" : "1px solid #D1D5DB",
                "&:hover": {
                  bgcolor: sourceType === "POLICY" ? "#EA6C0A" : "#F9FAFB",
                },
              }}
            />
          </Stack>
        </Stack>

        {sourceType === "POLICY" && (
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  fullWidth
                  label="Policy Number / SA ID Number"
                  placeholder="Enter policy number or SA ID number"
                  variant="outlined"
                  value={policyLookupValue}
                  onChange={(e: any) => {
                    const nextValue = e.target.value.trimStart();
                    const nextSearchType = resolvePolicySearchType(nextValue);
                    setPolicyLookupValue(nextValue);
                    setPolicySearchType(nextSearchType);
                    if (nextSearchType === "POLICY_NUMBER") {
                      setPolicyNumber(nextValue);
                    } else {
                      setPolicyNumber("");
                    }
                    setPolicyData(null);
                    setSelectedLivesInsured(null);
                    setMemberIdSearchValue("");
                    setPolicyError("");
                  }}
                  error={Boolean(policyError)}
                  helperText={policyError}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} display="flex" alignItems="flex-end">
                <ButtonComp
                  variant="contained"
                  sx={caseButtonSx}
                  onClick={handleLoadPolicy}
                  disabled={policyLoading}
                >
                  {policyLoading ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                      Loading...
                    </Box>
                  ) : (
                    "Load Policy"
                  )}
                </ButtonComp>
              </Grid>
            </Grid>

           

            {policyData && policySearchType !== "ID_NUMBER" && (
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Input
                      select
                      fullWidth
                      label="Who is the deceased person on this policy?"
                      variant="outlined"
                      value={selectedLivesInsuredOption?.key || ""}
                      onChange={(e: any) => {
                        const selected = livesInsuredOptions.find((option) => option.key === e.target.value);
                        handleLivesInsuredSelection(selected?.member || null);
                        setMemberIdSearchValue(
                          selected?.member ? getPolicyMemberIdentifier(selected.member) : "",
                        );
                      }}
                      error={Boolean(errors.selectedLivesInsured)}
                      helperText={errors.selectedLivesInsured}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (selected: any) =>
                          selected ? (
                            livesInsuredOptions.find((option) => option.key === selected)?.label || selected
                          ) : (
                            <span style={{ color: "#aaa" }}>Select main member, spouse, child, or dependent</span>
                          ),
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              backgroundColor: "#ffffff",
                              borderRadius: "12px",
                              maxHeight: "320px",
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="">
                        <span style={{ color: "#aaa" }}>Select main member, spouse, child, or dependent</span>
                      </MenuItem>
                      {livesInsuredOptions.map((option) => (
                        <MenuItem value={option.key} key={option.key}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Input>
                  </Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Input
                      fullWidth
                      label="Find Member By ID"
                      placeholder="Enter member ID number"
                      variant="outlined"
                      value={memberIdSearchValue}
                      onChange={(e: any) => handleMemberIdSearch(e.target.value.trimStart())}
                      error={Boolean(errors.selectedLivesInsured)}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </Box>}



      {classificationSelected && !canEditInformantForm && sourceType === "POLICY" && (
        <Box
          sx={{
            border: "1px dashed #D0D5DD",
            borderRadius: 2,
            p: 3,
            backgroundColor: "#FCFCFD",
          }}
        >
          <Typography fontSize={14} fontWeight={700} color="#344054" textAlign="left">
            Load the policy and choose who the deceased person is before the informant form becomes editable.
          </Typography>
        </Box>
      )}

{classificationSelected && canEditInformantForm && (
        <Grid
          container
          spacing={2.25}
          sx={{
            ...caseGridSx,
            opacity: canEditInformantForm ? 1 : 0.55,
            pointerEvents: canEditInformantForm ? "auto" : "none",
          }}
        >
          {/* First Name */}
          <Grid size={{ xs: 12, md: 3 }}>
            {/* <Input
            fullWidth
            label="Title"
            variant="outlined"
            value={informantDetails.title}
            onChange={(e: any) => {
              setInformantDetails({ title: e.target.value });
              clearError("title");
            }}
            error={Boolean(errors.title)}
            helperText={errors.title}
          /> */}
            <Input
              select
              fullWidth
              label="Title"
              variant="outlined"
              value={normalizeTitleValue(informantDetails.title || "")}
              onChange={(e: any) => {
                setInformantDetails({ title: e.target.value });
                clearError("title");
              }}
              error={Boolean(errors.title)}
              helperText={errors.title}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: any) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#aaa" }}>Select Title</span>
                  ),
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      maxHeight: "300px",
                    },
                  },
                },
              }}
            >
              {titleOptions.map((titleOption) => (
                <MenuItem
                  key={titleOption}
                  value={titleOption}
                  sx={{
                    background: "background.default",
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                  }}
                >
                  {String(titleOption).toUpperCase()}
                </MenuItem>
              ))}
            </Input>
          </Grid>
          {/* First Name */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Input
              fullWidth
              label="Initials"
              placeholder="Initials"
              variant="outlined"
              value={informantDetails.initials}
              onChange={(e: any) => {
                setInformantDetails({ initials: e.target.value });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Input
              fullWidth
              label="First Name"
              placeholder="First Name"
              variant="outlined"
              value={informantDetails.firstName}
              onChange={(e: any) => {
                setInformantDetails({ firstName: e.target.value });
                clearError("firstName");
              }}
              error={Boolean(errors.firstName)}
              helperText={errors.firstName}
            />
          </Grid>

          {/* Last Name */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Input
              fullWidth
              label="Last Name"
              placeholder="Last Name"
              variant="outlined"
              value={informantDetails.lastName}
              onChange={(e: any) => {
                setInformantDetails({ lastName: e.target.value });
                clearError("lastName");
              }}
              error={Boolean(errors.lastName)}
              helperText={errors.lastName}
            />
          </Grid>


          {/* Identity Type */}
          <Grid size={{ xs: 12, md: 3 }}>
            <RadioGroup
              row
              name="passport"
              value={informantDetails.identificationType}
              sx={caseRadioGroupSx}
              onChange={(e) => {
                setInformantDetails({ identificationType: e.target.value });
                clearError("identificationType");
              }}
            >
              <FormControlLabel value="id" control={<Radio />} label="ID" />
              <FormControlLabel
                value="passport"
                control={<Radio />}
                label="Passport"
              />
            </RadioGroup>
            <Input
              fullWidth
              variant="outlined"
              placeholder={
                informantDetails.identificationType === "passport"
                  ? "Passport Number"
                  : "ID Number"
              }
              value={informantDetails.identificationNumber}
              onChange={(e: any) => {
                const value = e.target.value;
                const updatedFields: Record<string, string> = {
                  identificationNumber: value,
                };

                if (informantDetails.identificationType === "id") {
                  const parsedDetails = extractSouthAfricanIdDetails(value);
                  if (parsedDetails) {
                    updatedFields.dateOfBirth = parsedDetails.dateOfBirth;
                    updatedFields.gender = parsedDetails.gender;
                    clearError("dateOfBirth");
                    clearError("gender");
                  }
                }

                setInformantDetails(updatedFields);
                clearError("identificationNumber");

                if (informantDetails.identificationType === "id") {
                  const error = validateSouthAfricanID(value);
                  setErrors((prev: any) => ({
                    ...prev,
                    identificationNumber: error || "",
                  }));
                } else {
                  setErrors((prev: any) => {
                    const newErrors = { ...prev };
                    delete newErrors.identificationNumber;
                    return newErrors;
                  });
                }
              }}
              error={Boolean(errors.identificationNumber)}
              helperText={errors.identificationNumber}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }} mt={1.8}>
            <Input
              select
              fullWidth
              label="Gender"
              variant="outlined"
              value={informantDetails.gender || ""}
              onChange={(e: any) => {
                setInformantDetails({ gender: e.target.value });
                clearError("gender");
              }}
              error={Boolean(errors.gender)}
              helperText={errors.gender}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: any) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#aaa" }}>Select Gender</span>
                  ),
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      maxHeight: "300px",
                    },
                  },
                },
              }}
            >
              <MenuItem
                value="Male"
                sx={{
                  background: "background.default",
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                Male
              </MenuItem>
              <MenuItem
                value="Female"
                sx={{
                  background: "background.default",
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                Female
              </MenuItem>
              <MenuItem
                value="Other"
                sx={{
                  background: "background.default",
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                Other
              </MenuItem>
            </Input>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }} mt={1.8}>
            <Input
  type="date"
  fullWidth
  label="Date of Birth"
  variant="outlined"
  value={informantDetails.dateOfBirth || ""}
  inputProps={{
    max: new Date().toISOString().split("T")[0],
  }}
  onChange={(e: any) => {
    setInformantDetails({
      dateOfBirth: e.target.value,
    });
    clearError("dateOfBirth");
  }}
  error={Boolean(errors.dateOfBirth)}
  helperText={errors.dateOfBirth}
/>
          </Grid>
          {/* <Grid size={{ xs: 12, md: 3 }}>
          <Input
            select
            fullWidth
            label="Marital status"
            variant="outlined"
            value={informantDetails.maritalStatus}
            onChange={(e: any) => {
              setInformantDetails({ maritalStatus: e.target.value });
              clearError("maritalStatus");
            }}
            error={Boolean(errors.maritalStatus)}
            helperText={errors.maritalStatus}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected: any) =>
                selected ? (
                  selected
                ) : (
                  <span style={{ color: "#aaa" }}>Select Marital Status</span>
                ),
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    maxHeight: "300px",
                  },
                },
              },
            }}
          >
            <MenuItem
              value="divorced"
              sx={{
                background: "background.default",
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              Divorced
            </MenuItem>
            <MenuItem
              value="married"
              sx={{
                background: "background.default",
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              Married
            </MenuItem>
            <MenuItem
              value="single"
              sx={{
                background: "background.default",
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              Single
            </MenuItem>
            <MenuItem
              value="widow"
              sx={{
                background: "background.default",
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              Widow
            </MenuItem>
            <MenuItem
              value="widower"
              sx={{
                background: "background.default",
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              Widower
            </MenuItem>
          </Input>
        </Grid> */}

          <Grid size={{ xs: 12, md: 3 }} mt={1.8}>
            <Input
              select
              fullWidth
              label="Relationship to deceased"
              variant="outlined"
              value={informantDetails.relationship}
              onChange={(e: any) => {
                setInformantDetails({ relationship: e.target.value });
                clearError("relationship");
              }}
              error={Boolean(errors.relationship)}
              helperText={errors.relationship}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: any) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#aaa" }}>Select Relationship</span>
                  ),
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      maxHeight: "300px",
                    },
                  },
                },
              }}
            >
              {loading ? (
                <MenuItem disabled>Loading relationships...</MenuItem>
              ) : (
                relationshipOptions.map((relation: string, index) => (
                  <MenuItem
                    key={index}
                    value={relation}
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    {relation}
                  </MenuItem>
                ))
              )}
            </Input>
          </Grid>

          {showBranchName && <Grid size={{ xs: 12, md: 3 }}>
            <Input
              select
              fullWidth
              label="Branch Name"
              variant="outlined"
              value={informantDetails.branchName || ""}
              onChange={(e: any) => {
                setInformantDetails({ branchName: e.target.value });
                clearError("branchName");
              }}
              error={Boolean(errors.branchName)}
              helperText={errors.branchName}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: any) =>
                  selected ? (
                    <Typography noWrap fontSize={14} color="#1F2937">
                      {selected}
                    </Typography>
                  ) : (
                    <Typography noWrap fontSize={14} color={theme.palette.grey[400]}>
                      Select Branch
                    </Typography>
                  ),
                MenuProps: {
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: {
                    sx: {
                      mt: 0.25,
                      width: "auto",
                      minWidth: 360,
                      maxWidth: "calc(100vw - 48px)",
                      maxHeight: 300,
                      borderRadius: "8px",
                      border: "1px solid #FB7A1A",
                      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.18)",
                      overflowY: "auto",
                      "& .MuiList-root": {
                        py: 0,
                      },
                      "&::-webkit-scrollbar": {
                        width: 8,
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#9CA3AF",
                        borderRadius: 8,
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#F3F4F6",
                      },
                    },
                  },
                },
              }}
            >
              {locationsLoading ? (
                <MenuItem disabled>Loading branches...</MenuItem>
              ) : branchOptions.length ? (
                branchOptions.map((branch: any) => (
                  <MenuItem
                    key={branch.id || branch.externalBranchId || branch.branchName}
                    value={branch.branchName}
                    sx={{
                      minHeight: 37,
                      px: 1.5,
                      py: 0.85,
                      borderRadius: 0,
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                      whiteSpace: "nowrap",
                      fontSize: 16,
                      fontWeight: 500,
                      color: "#111827",
                      textTransform: "uppercase",
                      bgcolor: "#FFFFFF",
                      "&:hover": {
                        bgcolor: "#FFF4EB",
                      },
                      "&.Mui-selected": {
                        bgcolor: "#FFF4EB",
                        color: "#EA6500",
                      },
                      "&.Mui-selected:hover": {
                        bgcolor: "#FFE8D4",
                      },
                    }}
                  >
                    {branch.branchName}
                    {branch.city || branch.province
                      ? ` - ${[branch.city, branch.province].filter(Boolean).join(", ")}`
                      : ""}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No branches found</MenuItem>
              )}
            </Input>
          </Grid>}

          <Grid size={{ xs: 12, md: 3 }}>
            <Input
              select
              fullWidth
              label="Nationality"
              variant="outlined"
              value={informantDetails.nationality}
              onChange={(e: any) => {
                setInformantDetails({ nationality: e.target.value });
                clearError("nationality");
              }}
              error={Boolean(errors.nationality)}
              helperText={errors.nationality}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: any) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#aaa" }}>Select Nationality</span>
                  ),
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      maxHeight: "300px",
                    },
                  },
                },
              }}
            >
              {countryOptions.length === 0 ? (
                <MenuItem disabled>Loading nationality...</MenuItem>
              ) : (
                nationalityOptions.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    {option}
                  </MenuItem>
                ))
              )}
            </Input>
          </Grid>

          {/* <Grid size={{ xs: 12, md: 4 }}>
          <Input
            select
            fullWidth
            label="Country of birth"
            variant="outlined"
            value={informantDetails.countryOfBirth}
            onChange={(e: any) => {
              setInformantDetails({ countryOfBirth: e.target.value });
              clearError("countryOfBirth");
            }}
            error={Boolean(errors.countryOfBirth)}
            helperText={errors.countryOfBirth}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected: any) =>
                selected ? (
                  selected
                ) : (
                  <span style={{ color: "#aaa" }}>Select Countries</span>
                ),
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    maxHeight: "300px",
                  },
                },
              },
            }}
          >
            {countryOptions.length === 0 ? (
              <MenuItem disabled>Loading countries...</MenuItem>
            ) : (
              residenceOptions.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{
                    background: "background.default",
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                  }}
                >
                  {option}
                </MenuItem>
              ))
            )}
          </Input>
        </Grid> */}

          <Grid size={{ xs: 12, md: 3 }}>
            <Input
              select
              fullWidth
              label="Country of residence"
              variant="outlined"
              value={informantDetails.countryOfResidence}
              onChange={(e: any) => {
                setInformantDetails({ countryOfResidence: e.target.value });
                clearError("countryOfResidence");
              }}
              error={Boolean(errors.countryOfResidence)}
              helperText={errors.countryOfResidence}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: any) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#aaa" }}>Select Countries</span>
                  ),
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      maxHeight: "300px",
                    },
                  },
                },
              }}
            >
              {countryOptions.length === 0 ? (
                <MenuItem disabled>Loading countries...</MenuItem>
              ) : (
                residenceOptions.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    {option}
                  </MenuItem>
                ))
              )}
            </Input>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Input
              fullWidth
              label="Email"
              placeholder="Email"
              variant="outlined"
              value={informantDetails.email}
              onChange={(e: any) => {
                setInformantDetails({ email: e.target.value });
                clearError("email");
              }}
              error={Boolean(errors.email)}
              helperText={errors.email}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Input
              fullWidth
              label="Address"
              placeholder="Address"
              variant="outlined"
              value={informantDetails.address}
              onChange={(e: any) => {
                setInformantDetails({ address: e.target.value });
                clearError("address");
              }}
              error={Boolean(errors.address)}
              helperText={errors.address}
            />
          </Grid>

          {/* <Grid size={{ xs: 12, md: 4 }}>
          <Input
            fullWidth
            label="Physical/Postal address"
            placeholder="Physical/Postal address"
            variant="outlined"
            value={informantDetails.postalAddress}
            onChange={(e: any) => {
              setInformantDetails({ postalAddress: e.target.value });
              clearError("postalAddress");
            }}
            error={Boolean(errors.postalAddress)}
            helperText={errors.postalAddress}
          />
        </Grid> */}

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="body1"
              sx={caseLabelSx}
            >
              Informant Cellphone number (for OTP verification)
            </Typography>
            <TextField
              fullWidth
              type="number"
              variant="outlined"
              value={informantDetails.contactNumber}
              placeholder="Enter Your Cellphone Number"
              onChange={(e: any) => {
                setIsOtpVerified(false);
                setInformantDetails({ contactNumber: e.target.value });
                clearError("contactNumber");
              }}
              error={Boolean(errors.contactNumber)}
              helperText={errors.contactNumber}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "var(--case-field-radius, 8px)",
                  height: "var(--case-field-height, 52px)",
                  backgroundColor: "#FCFCFD",
                },
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Select
                      value={countryCode || "+27"}
                      onChange={(e: any) => {
                        setCountryCode(e.target.value);
                        setInformantDetails({ countryCode: e.target.value });
                      }}
                      variant="standard"
                      disableUnderline
                      sx={{ width: 100 }}
                    >
                      {countryCodes.map((item) => (
                        <MenuItem key={item.code} value={item.code}>
                          {item.code}
                        </MenuItem>
                      ))}
                    </Select>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {isOtpVerified ? (
                      <img
                        src={check}
                        alt="check"
                        style={{ height: 25, width: 25 }}
                      />
                    ) : (
                      <ButtonComp
                        variant="contained"
                        width={{ xs: 104, md: 132 }}
                        height={34}
                        fontSize="13px"
                        textTransform="none"
                        sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}
                        onClick={handleOtpVerification}
                      >
                        Send OTP
                      </ButtonComp>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }} textAlign="left">
            <Typography sx={caseLabelSx}>
              Notifications
            </Typography>

            <Box sx={caseNotificationStackSx}>
              <FormControlLabel
                control={
                  <Switch
                    color="success"
                    checked={informantDetails.whatsappNotifications ?? false}
                    onChange={(e) =>
                      setInformantDetails({
                        whatsappNotifications: e.target.checked,
                      })
                    }
                  />
                }
                label={`WhatsApp: ${informantDetails.whatsappNotifications ? "Yes" : "No"
                  }`}
              />
              <FormControlLabel
                control={
                  <Switch
                    color="success"
                    checked={informantDetails.smsNotifications ?? false}
                    onChange={(e) =>
                      setInformantDetails({
                        smsNotifications: e.target.checked,
                      })
                    }
                  />
                }
                label={`SMS: ${informantDetails.smsNotifications ? "Yes" : "No"}`}
              />
            </Box>
          </Grid>

          <OtpPopup
            open={showOtp}
            otpValue={otpValue}
            onClose={() => setShowOtp(false)}
            onVerify={handleOtpVerify}
            getOtpValue={setOtpValue}
            cellPhoneNumber={cellPhoneNumber}
            onResendOtp={() => sendOtp(cellPhoneNumber)}
          />
          <VerifiedPopup
            label="Verified"
            open={showVerified}
            onClose={handleVerifiedClose}
          />
        </Grid>
      )}

      {/* Buttons */}
      <Box
        sx={{ ...caseActionBarSx, justifyContent: "flex-end" }}
      >
        {/* <ButtonComp variant="outlined" height={40} width={190}>
          Save as Draft
        </ButtonComp> */}

        <ButtonComp
          variant="contained"
          sx={caseButtonSx}
          onClick={handleNextClick}
          disabled={!classificationSelected || !canEditInformantForm}
        >
          Next
        </ButtonComp>
      </Box>
    </Paper>
  );
}
